import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Escenarios de simulación disponibles para el modo desarrollo
// ─────────────────────────────────────────────────────────────────────────────

const SIMULATION_SCENARIO = {
  ID_SUCCESS: 'id_success',           // INE válida, sin comprobante necesario
  ID_REQUIRES_POA: 'id_requires_poa', // Pasaporte → requiere comprobante
  ID_FAILURE: 'id_failure',           // Documento rechazado (score bajo)
  ID_EXPIRED: 'id_expired',           // Documento vencido
  DATA_MISMATCH: 'data_mismatch',     // Nombre del documento ≠ nombre de sesión → extraction_rejected
  POA_SUCCESS: 'poa_success',         // Comprobante válido → aprobado
  POA_CONDITIONAL: 'poa_conditional', // Comprobante score bajo → condicional
  POA_FAILURE: 'poa_failure',         // Comprobante rechazado
} as const;

type SimulationScenario = (typeof SIMULATION_SCENARIO)[keyof typeof SIMULATION_SCENARIO];

// Configuración completa del modo simulación
interface SimulationConfig {
  enabled: boolean;
  scenario: SimulationScenario;
}

// Valor por defecto: simulación desactivada para que los contextos reales funcionen
const SimulationContext = createContext<SimulationConfig>({
  enabled: false,
  scenario: SIMULATION_SCENARIO.ID_SUCCESS,
});

// Hook de consumo — lanza error si se usa fuera de un proveedor con enabled=true
function useSimulation(): SimulationConfig {
  return useContext(SimulationContext);
}

// Props del proveedor de simulación
interface SimulationProviderProps {
  children: ReactNode;
  scenario: SimulationScenario;
}

// Provider para envolver KYCFlowController en modo desarrollo
function SimulationProvider({ children, scenario }: SimulationProviderProps) {
  const value: SimulationConfig = {
    enabled: true,
    scenario,
  };

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

export { SimulationContext, SimulationProvider, useSimulation, SIMULATION_SCENARIO };
export type { SimulationScenario, SimulationConfig };
